import { MetricData } from './apiHooks';

/**
 * Cache configuration
 */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes for building reports
const CACHE_TTL_PUBLISHED_MS = 0; // Published reports always fetch fresh data (0 = no cache)
const DEFAULT_WIDGET_CACHE_VALIDITY_HOURS = 24; // 1 day default for predesigned cards

/**
 * Generate a cache key based on widget configuration and filters
 * This key determines if cached data is still valid
 */
export function generateCacheKey(
  widget,
  globalFilters,
  includeComparison = false
) {
  const config = widget.config;
  
  // Build key from all meaningful configuration that affects data
  const keyParts = [
    widget.type,
    widget.id,
    config.metricType || '',
    config.selectedSourceId || '',
    JSON.stringify(config.dateRange || {}),
    JSON.stringify(config.checkInThreshold || {}),
    JSON.stringify(config.engagementMetric || {}),
    includeComparison ? '1' : '0',
    includeComparison ? JSON.stringify(config.comparison || {}) : '',
    JSON.stringify(globalFilters || {}),
    // Include data source filters
    JSON.stringify(
      (widget.dataSources || [])
        .filter(ds => ds.enabled)
        .map(ds => ({
          sourceId: ds.sourceId,
          filters: ds.filters,
        }))
        .sort((a, b) => a.sourceId.localeCompare(b.sourceId))
    ),
  ];
  
  return keyParts.join('|');
}

/**
 * Check if cached data is still valid
 * @param cache Cache object from data source
 * @param currentCacheKey Current cache key based on current config
 * @param isPublished Whether the report is published
 * @returns true if cache is valid, false if it should be refreshed
 */
export function isCacheValid(
  cache,
  currentCacheKey,
  isPublished = false
) {
  if (!cache) return false;
  
  // Published reports always fetch fresh data
  if (isPublished) return false;
  
  // Cache key must match (config/filters haven't changed)
  if (cache.cacheKey !== currentCacheKey) return false;
  
  // Check if cache has expired
  const cacheAge = Date.now() - new Date(cache.timestamp).getTime();
  const ttl = isPublished ? CACHE_TTL_PUBLISHED_MS : CACHE_TTL_MS;
  
  return cacheAge < ttl;
}

/**
 * Update widget cache with new API response
 * @param widget Widget to update
 * @param data API response data
 * @param cacheKey Cache key for this data
 * @returns Updated widget
 */
export function updateWidgetCache(
  widget,
  data,
  cacheKey
) {
  // Update cache for each enabled data source
  const updatedDataSources = widget.dataSources.map((ds, index) => {
    if (!ds.enabled) return ds;
    
    const cachedData = data[index];
    if (!cachedData) return ds;
    
    return {
      ...ds,
      cache: {
        data: cachedData,
        timestamp: new Date().toISOString(),
        cacheKey,
      },
    };
  });
  
  return {
    ...widget,
    dataSources: updatedDataSources,
  };
}

/**
 * Get cached data from widget if available and valid
 * @param widget Widget to check for cache
 * @param currentCacheKey Current cache key
 * @param isPublished Whether report is published
 * @returns Cached data array or null if cache invalid/missing
 */
export function getCachedData(
  widget,
  currentCacheKey,
  isPublished = false
) {
  const enabledSources = widget.dataSources.filter(ds => ds.enabled);
  
  if (enabledSources.length === 0) return null;
  
  // Check if all enabled sources have valid cache
  const cachedData = enabledSources.map((ds) => {
    if (!ds.cache || !isCacheValid(ds.cache, currentCacheKey, isPublished)) {
      return null;
    }
    return ds.cache.data;
  });
  
  // If any source doesn't have valid cache, return null
  if (cachedData.some(data => data === null)) {
    return null;
  }
  
  return cachedData;
}

/**
 * Check if widget cache entry is still valid
 * @param cacheEntry Widget cache entry from report metadata
 * @param currentCacheKey Current cache key based on current config/filters
 * @param isPublished Whether the report is published
 * @returns true if cache is valid, false if it should be refreshed
 */
export function isWidgetCacheValid(
  cacheEntry: WidgetCacheEntry | undefined,
  currentCacheKey,
  isPublished = false
) {
  if (!cacheEntry) return false;
  
  // Published reports always fetch fresh data
  if (isPublished) return false;
  
  // Cache key must match (config/filters haven't changed)
  if (cacheEntry.cacheKey !== currentCacheKey) return false;
  
  // Check if cache has expired
  const cacheAge = Date.now() - new Date(cacheEntry.timestamp).getTime();
  const validityHours = cacheEntry.validityHours || DEFAULT_WIDGET_CACHE_VALIDITY_HOURS;
  const ttl = validityHours * 60 * 60 * 1000; // Convert hours to milliseconds
  
  return cacheAge < ttl;
}

/**
 * Get cached widget data from report if available and valid
 * @param report Report configuration
 * @param widgetId Widget ID
 * @param currentCacheKey Current cache key
 * @param isPublished Whether report is published
 * @returns Cached data or null if cache invalid/missing
 */
export function getWidgetCache(
  report,
  widgetId,
  currentCacheKey,
  isPublished = false
) {
  const cacheEntry = report.metadata?.widgetCache?.[widgetId];
  
  if (!cacheEntry || !isWidgetCacheValid(cacheEntry, currentCacheKey, isPublished)) {
    return null;
  }
  
  return cacheEntry.data;
}

/**
 * Update widget cache in report
 * @param report Report configuration
 * @param widgetId Widget ID
 * @param data API response data to cache
 * @param cacheKey Cache key for this data
 * @param validityHours Optional cache validity period in hours (default: 24)
 * @returns Updated report
 */
export function updateWidgetCacheInReport(
  report,
  widgetId,
  data,
  cacheKey,
  validityHours = DEFAULT_WIDGET_CACHE_VALIDITY_HOURS
) {
  const widgetCache = report.metadata?.widgetCache || {};
  
  return {
    ...report,
    metadata: {
      ...report.metadata,
      widgetCache: {
        ...widgetCache,
        [widgetId]: {
          data,
          timestamp: new Date().toISOString(),
          cacheKey,
          validityHours,
        },
      },
    },
  };
}

/**
 * Get cache timestamp for a widget (for display purposes)
 * @param report Report configuration
 * @param widgetId Widget ID
 * @returns ISO timestamp string or null if no cache exists
 */
export function getWidgetCacheTimestamp(
  report,
  widgetId
) {
  return report.metadata?.widgetCache?.[widgetId]?.timestamp || null;
}

